import dayjs from "dayjs";
import { TournamentPage, TQuiz } from "../types/tournamentTypes";
import { User } from "../types/userTypes";
import { darkGreen, darkYellow, paleGreen, paleYellow } from "./colors";

export const tournamentSpecialChars = new Set([
  "\n",
  "[",
  "]",
  "\\",
  "'",
  "*",
  "`",
]);

/**
 * checks if the user can edit the tournament page. Does not validate against current data.
 * @param page the page being edited
 * @param user the user attempting the edit
 * @returns if the user has write access to the page
 */
export const canEditPage = (page: TournamentPage, user?: User): boolean => {
  if (!user) return false;
  if (user.securityClearance.logonType === "ADMIN") return true;
  if (
    "ministry" in page &&
    user.securityClearance.tournamentAccess?.includes(page.ministry)
  )
    return true;
  return false;
};

/**
 * uses data in a page to get a description
 * @param page the page
 * @returns description text for top of the page
 */
export const getPageDescription = (page: TournamentPage): string => {
  // Description overrides everything
  if (page.description) return page.description;
  let result = "";

  const addShortBio = () => {
    // Add the short bio to any page
    if (page.additionalData.shortBio) {
      result = `${result} ${page.additionalData.shortBio}`;
    }
  };
  switch (page.pageType) {
    case "TOURNAMENT":
      const isOnline = page.location === "Online";
      const isIndividual = page.isIndividual === true;

      result = `${getPageName(page)} is a`;

      // Time variation
      const now = dayjs().startOf("day");
      const isNotFuture = page.startDate && now >= dayjs(page.startDate);
      const isNotPast = page.endDate && now <= dayjs(page.endDate);
      if (isNotPast) {
        result = `${result}${isNotFuture ? "n ongoing" : "n upcoming"}`;
      }
      if ((isOnline || isIndividual) && !isNotPast) result += "n";
      if (isOnline) result += " online";
      if (isIndividual) result += " individual";

      // Final details
      result = `${result} tournament`;
      if (page.host) result = `${result} hosted by [[${page.host}]]`;
      if (page.location && !isOnline)
        result = `${result} held in ${page.location}`;
      result += ".";
      addShortBio();

      if (isNotFuture && isNotPast)
        result += `\n\nThe most up-to-date statistics and quizzes may be available at [[www.quizzing.live/scores/${
          page.additionalData?.account || ""
        }|quizzing.live]].`;
      break;
    case "QUIZZER":
      result = `${getPageName(page)} is a ${
        page.lastActive ? "former " : ""
      }quizzer from [[${page.ministry}]]`;
      result += ".";
      break;
    case "TEAM":
      result = `${getPageName(page)} ${page.lastActive ? "was" : "is"} a team`;
      if (page.ministry) {
        result += ` quizzing for [[${page.ministry}]]`;
      }
      result += ".";
      break;
    case "MINISTRY":
      result = `${page.ministryName} (${page.pageId}) ${
        page.lastActive ? "was" : "is"
      } a ministry based in ${page.location}`;
      result += ".";
      break;
    default:
      break;
  }

  if (page.pageType !== "TOURNAMENT") addShortBio();
  return result;
};

export const removeMarkdown = (text: string) => {
  let result = text;
  let isSkipped = false;
  let phase = "TEXT";
  let linkText = "";
  let linkStart = 0;
  for (let i = 0; i < result.length; i++) {
    const char = result.charAt(i);
    if (char === "\n" && phase === "TEXT") {
      // New line; replace with space
      result = result.substring(0, i) + " " + result.substring(i + 2);
      continue;
    }
    if (char === "\\") {
      // Escaped character; ignore the escape and keep the character by skipping the removal
      i++;
      isSkipped = true;
      continue;
    }

    if (tournamentSpecialChars.has(char)) {
      if (isSkipped) {
        isSkipped = false;
      } else if (char === "[" && phase === "TEXT") {
        // Start of link
        linkStart = i - 1;
        i++;
        phase = "LINK";
        linkText = "";
      } else if (char === "]" && phase === "LINK") {
        // End of link
        const newIndex = linkStart + linkText.length;
        result =
          result.substring(0, linkStart) +
          " " +
          linkText +
          result.substring(i + 2);
        i = newIndex;
        phase = "TEXT";
      } else if (result.charAt(i + 1) === char) {
        // double character; skip the characters but keep the text
        result = result.substring(0, i) + result.substring(i + 2);
        i -= 1;
      }
    } else if (phase === "LINK") {
      // No special character, but in a link
      if (char === "|") {
        // Alias text; use that instead of the actual link
        linkText = "";
      } else if (char === "/") {
        // remove break to secondary ID
        linkText += " ";
      } else {
        linkText += char;
      }
    }
  }
  return result;
};
export const getPageName = (page: TournamentPage): string => {
  if (page.additionalData?.pageTitle) return page.additionalData.pageTitle;
  if (page.pageType === "MINISTRY") {
    return page.ministryName;
  }
  return `${page.pageId}${page.secondaryId ? ` ${page.secondaryId}` : ""}`;
};

export const getTeamNameDisplay = (
  teamName: string,
  ministry?: string
): string => {
  let nameResult = teamName;
  if (ministry) {
    nameResult = `${nameResult} - ${ministry}`;
  }
  return nameResult;
};

export const getAdjacentSId = (tournament: TournamentPage) => {
  let previous = "";
  let next = "";
  let previousDisplay = "";
  let nextDisplay = "";
  const sIdNumeric = Number.parseInt(tournament.secondaryId);
  const isNumericSId = !isNaN(sIdNumeric);

  if ("previousTournament" in tournament.additionalData) {
    const previousParts =
      tournament.additionalData.previousTournament.split("|");
    previous = previousParts[0];
    previousDisplay = previousParts[1];
  } else if (isNumericSId && sIdNumeric > 1) {
    previous = String(sIdNumeric - 1);
  }
  if ("nextTournament" in tournament.additionalData) {
    const nextParts = tournament.additionalData.nextTournament.split("|");
    next = nextParts[0];
    nextDisplay = nextParts[1];
  } else if (isNumericSId) {
    next = String(sIdNumeric + 1);
  }
  return { previous, next, previousDisplay, nextDisplay };
};

export const getTQuizBackground = (quiz: TQuiz, lightMode: boolean) => {
  let backgroundColor = "";
  if (quiz.flag === "W") {
    backgroundColor = lightMode ? paleYellow : darkYellow;
  } else if (quiz.flag === "C") {
    backgroundColor = lightMode ? paleGreen : darkGreen;
  }
  return backgroundColor;
};
